import {
  ChevronLeft,
  ChevronRight,
  Loader2Icon,
  PlusIcon,
  TrashIcon,
} from "lucide-react";
import { useEffect } from "react";
import { useRef } from "react";
import { Button } from "src/components/shadcn/button";
import { Card } from "src/components/shadcn/card";
import {
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "src/components/shadcn/form";
import { cn } from "src/lib/utils";
import { Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

export const ImageUploadCarousel = ({
  isLoading,
  filesData,
  onFileChange,
  onRemoveImage,
  formControl,
  name = "file",
}) => {
  const swiperRef = useRef(null);

  useEffect(() => {
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.update();
      if (swiperRef.current.swiper.pagination) {
        swiperRef.current.swiper.pagination.update();
      }
    }
  }, [filesData]);

  return (
    <div className="flex flex-col gap-2 w-full h-full">
      <div className="flex xs:flex-row flex-col xs:items-end justify-between gap-x-4 gap-y-2">
        <FormField
          control={formControl}
          name={name}
          render={() => (
            <FormItem className="flex items-center flex-wrap justify-between xs:justify-start gap-x-2 gap-y-1 space-y-0">
              <FormLabel>Foto Bukti</FormLabel>
              <FormDescription>
                {filesData.length === 0
                  ? "Belum ada foto"
                  : `Total ${filesData.length} foto`}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <Button
          type="button"
          className="relative flex items-center gap-2 hover:cursor-pointer"
          disabled={isLoading}
        >
          <input
            className={cn(
              isLoading ? "hover:cursor-not-allowed" : "hover:cursor-pointer",
              "absolute left-0 top-0 w-full h-full opacity-0",
            )}
            type="file"
            accept="image/*"
            multiple
            disabled={isLoading}
            onChange={onFileChange}
            onClick={(e) => {
              e.target.value = "";
            }}
          />
          <PlusIcon />
          <span>Tambah</span>
        </Button>
      </div>

      <div className="grid relative h-full w-full items-center">
        {filesData.length > 1 && (
          <>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="absolute top-1/2 left-2 z-[50] -translate-x-1/2 -translate-y-1/2"
              onClick={() => swiperRef.current?.swiper?.slidePrev()}
              disabled={
                isLoading ||
                (filesData.length > 1 &&
                  !swiperRef.current?.swiper?.params.loop &&
                  swiperRef.current?.swiper?.isBeginning)
              }
            >
              <ChevronLeft size={16} />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="absolute top-1/2 right-2 z-[50] translate-x-1/2 -translate-y-1/2"
              onClick={() => swiperRef.current?.swiper?.slideNext()}
              disabled={
                isLoading ||
                (filesData.length > 1 &&
                  !swiperRef.current?.swiper?.params.loop &&
                  swiperRef.current?.swiper?.isEnd)
              }
            >
              <ChevronRight size={16} />
            </Button>
          </>
        )}
        <Swiper
          ref={swiperRef}
          spaceBetween={25}
          className="relative overflow-hidden h-full w-full [&_.swiper-pagination]:text-balance [&_.swiper-pagination]:pb-2 [&_.swiper-pagination]:space-y-2 [&_.swiper-pagination]:space-x-2"
          slidesPerView={1}
          loop={filesData.length > 1}
          modules={[Pagination]}
          pagination={{
            clickable: true,
            renderBullet: function (index, className) {
              return `<span class="!size-6 !text-sm !rounded-full !aspect-square !bg-white !border !border-gray-300 ${className}">${index + 1}</span>`;
            },
          }}
        >
          {filesData.length === 0 && (
            <SwiperSlide>
              <Card className="relative flex bg-transparent h-full min-h-96 text-merah-hover border-2 border-dashed flex-col items-center justify-center gap-4 w-full rounded-xl border-merah-hover hover:border-solid hover:bg-merah-hover/5">
                <input
                  className={cn(
                    isLoading
                      ? "hover:cursor-not-allowed"
                      : "hover:cursor-pointer",
                    "absolute left-0 top-0 w-full h-full opacity-0",
                  )}
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={isLoading}
                  onChange={onFileChange}
                  onClick={(e) => {
                    e.target.value = "";
                  }}
                />
                {isLoading ? (
                  <Loader2Icon size={24} className="animate-spin" />
                ) : (
                  <PlusIcon size={24} />
                )}
                <div className="flex flex-col gap-0 px-8">
                  <h3 className="font-semibold text-lg text-center">
                    {isLoading ? "Memproses..." : "Foto Bukti"}
                  </h3>
                  <p className="font-light text-sm text-center">
                    {isLoading
                      ? "Sedang memproses..."
                      : "Unggah bukti berupa foto di sini"}
                  </p>
                </div>
              </Card>
            </SwiperSlide>
          )}
          {filesData.map((fileData, index) => (
            <SwiperSlide
              className="flex !w-full"
              key={`${index}-image-${fileData.previewUrl.slice(-10)}`}
            >
              <Card className="flex relative min-h-96 w-full group h-full overflow-y-auto !object-contain bg-transparent text-merah-hover border-2 flex-col gap-4 rounded-xl border-merah-hover">
                <div className="size-full absolute group-hover:bg-black/10 z-10 transition-colors" />
                <div className="flex items-center sticky top-3 right-4 w-fit ml-auto mb-auto gap-2 z-20">
                  <Button
                    onClick={() => onRemoveImage(index)}
                    type="button"
                    size="icon"
                    variant="destructive"
                    disabled={isLoading}
                  >
                    <TrashIcon />
                  </Button>
                </div>
                <img
                  className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-full w-auto -z-10 object-contain p-1"
                  src={fileData.previewUrl}
                  alt={`Preview ${index + 1}`}
                />
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};
